import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { PartnersService } from './partnersApi';
import { IPartners } from '../../../../interfaces/partners/IPartners';
import { IAddress } from '../../../../interfaces/auth/ICreateUserSchema';
import { IEditPartner } from '../../../../interfaces/partners/IEditPartner';
import { IPartnersSchema } from '../../../../interfaces/partners/IPartnersSchema';
import { IPartnersListState } from '../../../../interfaces/partners/IPartnersList';
import { IAdminEditPartner } from '../../../../interfaces/partners/IAdminEditPartner';
import { PaginatedInvitedPartners, PaginatedPartners } from '../../../../interfaces/partners/paginatedStore.type';
import { ApiResponse } from '../../../../interfaces/models/models/paginated-response.model';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';

const initialState: IPartnersListState = {
    partnersAreLoading: false,
    invitedAreLoading: false,
    toastSuccessRequest: false,
    partnerIsLoading: false,
    partnerNavigationLoading: false,
    requestStatus: 'default',
    agentsAreLoading: false,
    partnerMarketsAreLoading: false,
    message: '',
};

export const tryToFetchPartners = createAsyncThunk<PaginatedPartners, IPaginationPayload>(
    'partners/tryToFetchPartners',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await PartnersService.tryToFetchPartners(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchInvitedPartners = createAsyncThunk<PaginatedInvitedPartners, IPaginationPayload>(
    'partners/tryToFetchInvitedPartners',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await PartnersService.tryToFetchInvitedPartners(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchPartnerNavigation = createAsyncThunk<any, undefined | null>(
    'partners/tryToFetchPartnerNavigation',
    async () => {
        const result = await PartnersService.tryToFetchPartnerNavigation();
        return result?.data;
    }
);

export const tryToFetchUserNotifications = createAsyncThunk<any, any>(
    'partners/tryToFetchUserNotifications',
    async ({ data, page, size }) => {
        const result = await PartnersService.tryToFetchUserNotifications(data, page, size);
        return result?.data?.data;
    }
);

export const tryToResendInvitePartner = createAsyncThunk<any, any>(
    'partners/tryToResendInvitePartner',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToResendInvitePartner(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToSetReadNotifications = createAsyncThunk<any, any>(
    'partners/tryToSetReadNotifications',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToSetReadNotifications(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToInviteNewPartner = createAsyncThunk<any, any>(
    'partners/tryToInviteNewPartner',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToInviteNewPartner(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToFetchSinglePartner = createAsyncThunk<IPartners, string>(
    'partners/tryToFetchSinglePartner',
    async (id: string) => {
        const result = await PartnersService.tryToFetchSinglePartner(id);
        return result?.data?.data;
    },
);

export const tryToFetchAllPartners = createAsyncThunk<IPartners[], any>(
    'partners/tryToFetchAllPartners',
    async () => {
        const result = await PartnersService.tryToFetchAllPartners();
        return result?.data;
    },
);

export const tryToAddPartner = createAsyncThunk<ApiResponse<IPartnersSchema>, IPartners>(
    'partners/tryToAddPartner',
    async (data: IPartners, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToAddPartner(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToRequestToRegisterPartner = createAsyncThunk<ApiResponse<IPartnersSchema>, IPartners>(
    'partners/tryToRequestToRegisterPartner',
    async (data: IPartners, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToRequestToRegisterPartner(
                data,
            );
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToFetchUserData = createAsyncThunk<any, undefined>(
    'partners/tryToFetchUserData',
    async () => {
        const result = await PartnersService.tryToFetchUserData();
        return result?.data;
    },
);

export const tryToEditPartner = createAsyncThunk<ApiResponse<IPartnersSchema>, IEditPartner>(
    'partners/tryToEditPartner',
    async (data: IEditPartner, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToEditPartner(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEditPartnerDetails = createAsyncThunk<ApiResponse<IPartnersSchema>, IAdminEditPartner>(
    'partners/tryToEditPartnerDetails',
    async (data: IAdminEditPartner, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToEditPartnerDetails(
                data?._id || '',
                data,
            );
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEditAddressPartner = createAsyncThunk<ApiResponse<IPartnersSchema>, IAddress>(
    'partners/tryToEditAddressPartner',
    async (data: IAddress, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToEditAddressPartner(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToSetAddressDefault = createAsyncThunk<ApiResponse<IPartnersSchema>, string>(
    'partners/tryToSetAddressDefault',
    async (id: string, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToSetAddressDefault(id);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEnablePartner = createAsyncThunk<ApiResponse<IPartnersSchema>, string>(
    'partners/tryToEnablePartner',
    async (id, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToEnablePartner(id);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToDisablePartner = createAsyncThunk<ApiResponse<IPartnersSchema>, string>(
    'partners/tryToDisablePartner',
    async (id, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToDisablePartner(id);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToDeletePartner = createAsyncThunk<ApiResponse<IPartnersSchema>, string>(
    'partners/tryToDeletePartner',
    async (id, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToDeletePartner(id);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToChangeActiveMarket = createAsyncThunk<any, { marketId: string }>(
    'stores/tryToChangeActiveMarket',
    async (data) => {
        return data?.marketId;
    }
);

export const tryToAuthorizeUser = createAsyncThunk<any, { dashboard: string }>(
    'partners/tryToAuthorizeUser',
    async (data) => {
        const result = await PartnersService.tryToAuthorizeUser(data);
        return result?.data;
    },
);

export const tryToGenerateApiKey = createAsyncThunk<any>(
    'partners/tryToGenerateApiKey',
    async () => {
        const result = await PartnersService.tryToGenerateApiKey();
        return result?.data;
    },
);

export const tryToAddGeneratedApiKey = createAsyncThunk<any, any>(
    'partners/tryToAddGeneratedApiKey',
    async (data: any, { rejectWithValue }) => {
        try {
            const result = await PartnersService.tryToAddGeneratedApiKey(data);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const partnersSlice = createSlice({
    name: 'partners',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchPartners
            .addCase(tryToFetchPartners.pending, (state) => {
                state.partnersAreLoading = true;
            })
            .addCase(tryToFetchPartners.rejected, (state) => {
                state.partnersAreLoading = false;
            })
            .addCase(tryToFetchPartners.fulfilled, (state, action) => {
                state.partnersAreLoading = false;
                state.partners = action.payload;
            })

            // tryToFetchInvitedPartners
            .addCase(tryToFetchInvitedPartners.pending, (state) => {
                state.invitedAreLoading = true;
            })
            .addCase(tryToFetchInvitedPartners.rejected, (state) => {
                state.invitedAreLoading = false;
            })
            .addCase(tryToFetchInvitedPartners.fulfilled, (state, action) => {
                state.invitedAreLoading = false;
                state.invitedPartners = action.payload;
            })

            // tryToFetchUserData
            .addCase(tryToFetchUserData.rejected, (state) => {
                state.userData = undefined;
            })
            .addCase(tryToFetchUserData.fulfilled, (state, action) => {
                state.userData = action.payload?.data;
            })

            // tryToFetchSinglePartner
            .addCase(tryToFetchSinglePartner.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToFetchSinglePartner.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToFetchSinglePartner.fulfilled, (state, action) => {
                state.partnerIsLoading = false;
                state.partnerDetail = action.payload;
            })

            // tryToAddPartner
            .addCase(tryToAddPartner.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToAddPartner.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToAddPartner.fulfilled, (state, action) => {
                state.partnerIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg;
                if (state.partners?.data)
                    state.partners.data.elements = [
                        ...state.partners?.data.elements,
                        data,
                    ];
            })

            // tryToRequestToRegisterPartner
            .addCase(tryToRequestToRegisterPartner.pending, (state) => {
                state.partnerIsLoading = true;
                state.toastSuccessRequest = false;
            })
            .addCase(tryToRequestToRegisterPartner.rejected, (state) => {
                state.partnerIsLoading = false;
                state.toastSuccessRequest = false;
            })
            .addCase(
                tryToRequestToRegisterPartner.fulfilled,
                (state, action) => {
                    state.partnerIsLoading = false;
                    state.requestStatus = 'success';
                    state.toastSuccessRequest = true;
                },
            )

            // tryToEditPartner
            .addCase(tryToEditPartner.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToEditPartner.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToEditPartner.fulfilled, (state) => {
                state.partnerIsLoading = false;
            })

            // tryToEditAddressPartner
            .addCase(tryToEditAddressPartner.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToEditAddressPartner.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToEditAddressPartner.fulfilled, (state) => {
                state.partnerIsLoading = false;
            })

            // tryToSetAddressDefault
            .addCase(tryToSetAddressDefault.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToSetAddressDefault.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToSetAddressDefault.fulfilled, (state) => {
                state.partnerIsLoading = false;
            })

            // tryToAddGeneratedApiKey
            .addCase(tryToAddGeneratedApiKey.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToAddGeneratedApiKey.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToAddGeneratedApiKey.fulfilled, (state, action) => {
                state.partnerIsLoading = false;
                state.requestStatus = 'success';
                if (state.partners?.data.elements)
                    state.partners.data.elements =
                        { ...state }.partners?.data.elements.map((partner: any) => {
                            if (partner?._id === action.meta?.arg?.partnerId) {
                                return {
                                    ...partner,
                                    apiIntegrationKey:
                                        action.meta.arg.apiIntegrationKey,
                                };
                            } else {
                                return partner;
                            }
                        }) || [];
            })

            // tryToDisablePartner
            .addCase(tryToDisablePartner.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToDisablePartner.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToDisablePartner.fulfilled, (state, action) => {
                state.partnerIsLoading = false;
                state.requestStatus = 'success';
                if (state.partners?.data.elements)
                    state.partners.data.elements =
                        { ...state }.partners?.data.elements.map((partner: any) => {
                            if (partner?._id === action.meta?.arg) {
                                return {
                                    ...partner,
                                    enabled: false,
                                };
                            } else {
                                return partner;
                            }
                        }) || [];
            })

            // tryToResendInvitePartner
            .addCase(tryToResendInvitePartner.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToResendInvitePartner.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToResendInvitePartner.fulfilled, (state, action) => {
                state.partnerIsLoading = false;
                state.requestStatus = 'success';
                if (state.invitedPartners?.data.elements)
                    state.invitedPartners.data.elements =
                        { ...state }.invitedPartners?.data.elements.map((partner: any) => {
                            if (partner?.token === action.meta?.arg?.data?.token) {
                                return {
                                    ...partner,
                                    used: true
                                };
                            } else {
                                return partner;
                            }
                        }) || [];
            })

            // tryToEnablePartner
            .addCase(tryToEnablePartner.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToEnablePartner.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToEnablePartner.fulfilled, (state, action) => {
                state.partnerIsLoading = false;
                state.requestStatus = 'success';
                if (state.partners?.data.elements)
                    state.partners.data.elements =
                        { ...state }.partners?.data.elements.map((partner: any) => {
                            if (partner?._id === action.meta?.arg) {
                                return {
                                    ...partner,
                                    enabled: true,
                                };
                            } else {
                                return partner;
                            }
                        }) || [];
            })

            // tryToDeletePartner
            .addCase(tryToDeletePartner.pending, (state) => {
                state.partnerIsLoading = true;
            })
            .addCase(tryToDeletePartner.rejected, (state) => {
                state.partnerIsLoading = false;
            })
            .addCase(tryToDeletePartner.fulfilled, (state, action) => {
                state.partnerIsLoading = false;
                state.requestStatus = 'success';
                if (state.partners?.data.elements)
                    state.partners.data.elements =
                        state.partners?.data.elements.filter(
                            (partner: any) => partner?._id !== action.meta?.arg,
                        );
            })
    },
});

export default partnersSlice.reducer;
